/**
 * caseThread.js — the ONE answer to "which messages in this thread are mine?"
 * v242.157.0, PATIENT-OWN-MESSAGE-INVISIBLE-1.
 *
 * ★★★ IT EXISTS BECAUSE NOTHING OWNED THE QUESTION, AND THE SAME MISTAKE WAS
 *   MADE THREE TIMES IN THREE DAYS. A case thread has two directions. Every
 *   place that has had to tell them apart has re-derived the answer inline, and
 *   each time the derivation kept ONE direction and silently dropped the other:
 *
 *     · the MD panel's nav badge counted the PATIENT's event in the DOCTOR's
 *       feed (D343);
 *     · the API comment claimed one notification type served both;
 *     · and `useAgentStore.loadCaseMessages` fetched the whole thread and then
 *       kept only `role === 'md'`, so the patient's own message — which the
 *       server had, correctly, under `patient_via_agent` — was fetched and
 *       thrown away. When theirs was the ONLY message in the thread, the
 *       `length === 0` early return meant nothing merged at all.
 *
 *   Dr. Rami found the third one on the first end-to-end use of the messenger:
 *   "did send but did not save the message I sent in the chat to see."
 *
 * ★★ A THIRD OCCURRENCE IS NOT THREE MISTAKES, IT IS A MISSING PREDICATE
 *   (§1dl(c), §1do(f)). This file is the predicate. If you are about to write
 *   `m.role === 'md'` or `m.role === 'patient_via_agent'` anywhere else, that is
 *   the bug this file was created to end — import from here instead, and
 *   `scripts/check-case-thread.mjs` is the arm that says so out loud.
 *
 * ★ THE SERVER AND THE CHAT DO NOT SHARE A VOCABULARY, WHICH IS HALF OF WHY THIS
 *   KEPT HAPPENING. The server writes `md` / `patient_via_agent` into
 *   `case_messages`; the chat renders `md_message` (the doctor's bubble) and
 *   `user` (the patient's own). `threadBubble` is the ONLY translation between
 *   them, so the two vocabularies can never drift apart in two places at once.
 */

/** Roles the API writes into `cases.case_messages`. */
export const SERVER_ROLE_MD = 'md'
export const SERVER_ROLE_PATIENT = 'patient_via_agent'

/** Roles the in-app conversation renders. */
export const BUBBLE_ROLE_MD = 'md_message'
export const BUBBLE_ROLE_PATIENT = 'user'

export function isMdMessage(m) {
  return m?.role === SERVER_ROLE_MD
}

export function isPatientMessage(m) {
  return m?.role === SERVER_ROLE_PATIENT
}

/**
 * Both directions of the thread, in the order the server gave them.
 * ★ Anything else (a status row, a future role) is deliberately excluded rather
 *   than defaulted into one side — defaulting is what put the patient's event in
 *   the doctor's feed in D343.
 */
export function threadMessages(remote) {
  return (remote || []).filter((m) => isMdMessage(m) || isPatientMessage(m))
}

/**
 * A server row as the chat renders it. The doctor's messages carry an attributed
 * name; the patient's own are their own bubble and need none.
 */
export function threadBubble(m) {
  const mine = isPatientMessage(m)
  return {
    role: mine ? BUBBLE_ROLE_PATIENT : BUBBLE_ROLE_MD,
    content: m.body,
    timestamp: new Date(m.created_at).getTime(),
    _serverId: m.id,
    ...(mine ? {} : { mdName: m.created_by?.name || 'your physician' }),
    threadId: m.thread_id,
  }
}

/**
 * Every server id already on screen, in EITHER direction.
 * ★★ THE OLD DEDUPE READ ONLY `md_message` BUBBLES. Once the patient's own
 *   messages carry a `_serverId` too, a dedupe that looks at one role would
 *   re-add theirs on every single chat open. The question is "is this row
 *   already here", not "is this doctor row already here".
 */
export function seenServerIds(messages) {
  return new Set((messages || []).filter((m) => m?._serverId).map((m) => m._serverId))
}
